import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { isPublicMoorageListing } from '../../util/listingHelpers';
import {
  getTransactionUpcomingDate,
  getUpcomingThreshold,
  selectNextUpcomingTransaction,
} from '../../util/upcomingBooking';
import {
  BOOKING_STATE_ACCEPTED,
  BOOKING_STATE_PENDING,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
} from '../../util/types';

import { fetchCurrentUser } from '../../ducks/user.duck';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;
const MANAGEABLE_LISTING_STATES = [
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
].join(',');
const ACTIVE_BOOKING_STATES = [BOOKING_STATE_PENDING, BOOKING_STATE_ACCEPTED];
const TRANSACTIONS_PAGE_SIZE = 100;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const FETCH_UPCOMING_TRANSACTIONS_REQUEST =
  'app/ManageListingsPage/FETCH_UPCOMING_TRANSACTIONS_REQUEST';
export const FETCH_UPCOMING_TRANSACTIONS_SUCCESS =
  'app/ManageListingsPage/FETCH_UPCOMING_TRANSACTIONS_SUCCESS';
export const FETCH_UPCOMING_TRANSACTIONS_ERROR =
  'app/ManageListingsPage/FETCH_UPCOMING_TRANSACTIONS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const DISCARD_DRAFT_REQUEST = 'app/ManageListingsPage/DISCARD_DRAFT_REQUEST';
export const DISCARD_DRAFT_SUCCESS = 'app/ManageListingsPage/DISCARD_DRAFT_SUCCESS';
export const DISCARD_DRAFT_ERROR = 'app/ManageListingsPage/DISCARD_DRAFT_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';
export const CLEAR_OPEN_LISTING_ERROR = 'app/ManageListingsPage/CLEAR_OPEN_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  upcomingTransactions: [],
  upcomingTransactionsInProgress: false,
  upcomingTransactionsError: null,
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  discardingDraft: null,
  discardingDraftError: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAR_OPEN_LISTING_ERROR:
      return {
        ...state,
        openingListing: null,
        openingListingError: null,
      };

    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case FETCH_UPCOMING_TRANSACTIONS_REQUEST:
      return {
        ...state,
        upcomingTransactionsInProgress: true,
        upcomingTransactionsError: null,
      };
    case FETCH_UPCOMING_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        upcomingTransactions: payload.transactions,
        upcomingTransactionsInProgress: false,
      };
    case FETCH_UPCOMING_TRANSACTIONS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        upcomingTransactions: [],
        upcomingTransactionsInProgress: false,
        upcomingTransactionsError: payload,
      };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case DISCARD_DRAFT_REQUEST:
      return {
        ...state,
        discardingDraft: payload.listingId,
        discardingDraftError: null,
      };
    case DISCARD_DRAFT_SUCCESS:
      return {
        ...state,
        discardingDraft: null,
      };
    case DISCARD_DRAFT_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        discardingDraft: null,
        discardingDraftError: {
          listingId: state.discardingDraft,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

export const clearOpenListingError = () => ({
  type: CLEAR_OPEN_LISTING_ERROR,
});

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const discardDraftRequest = listingId => ({
  type: DISCARD_DRAFT_REQUEST,
  payload: { listingId },
});

export const discardDraftSuccess = () => ({
  type: DISCARD_DRAFT_SUCCESS,
});

export const discardDraftError = e => ({
  type: DISCARD_DRAFT_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const upcomingTransactionsRequest = () => ({
  type: FETCH_UPCOMING_TRANSACTIONS_REQUEST,
});

export const upcomingTransactionsSuccess = transactions => ({
  type: FETCH_UPCOMING_TRANSACTIONS_SUCCESS,
  payload: { transactions },
});

export const upcomingTransactionsError = e => ({
  type: FETCH_UPCOMING_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

const listingUuid = listing => listing?.id?.uuid || listing?.uuid;
const listingUuidSet = listings => new Set(listings.map(listingUuid).filter(Boolean));

export const selectUpcomingTransactionsForListings = (transactions, listings, now = new Date()) => {
  const transactionsByListingId = transactions.reduce((result, transaction) => {
    const listingId = transaction?.listing?.id?.uuid;
    if (listingId) {
      result[listingId] = result[listingId] || [];
      result[listingId].push(transaction);
    }
    return result;
  }, {});
  const listingById = listings.reduce((result, listing) => {
    result[listingUuid(listing)] = listing;
    return result;
  }, {});

  return listings
    .map(listing => {
      const listingId = listingUuid(listing);
      return selectNextUpcomingTransaction(transactionsByListingId[listingId] || [], listing, now);
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aDate = getTransactionUpcomingDate(a, listingById[a?.listing?.id?.uuid]);
      const bDate = getTransactionUpcomingDate(b, listingById[b?.listing?.id?.uuid]);
      return aDate.getTime() - bDate.getTime();
    });
};

export const fetchUpcomingTransactionsForListings = listings => (dispatch, getState, sdk) => {
  const bookingListings = listings.filter(
    listing => !isPublicMoorageListing(listing?.attributes?.publicData || {})
  );
  const bookingListingUuidSet = listingUuidSet(bookingListings);

  if (bookingListingUuidSet.size === 0) {
    dispatch(upcomingTransactionsSuccess([]));
    return Promise.resolve([]);
  }

  dispatch(upcomingTransactionsRequest());

  const now = new Date();
  const queryStart = bookingListings.reduce((earliest, listing) => {
    const threshold = getUpcomingThreshold(listing, now);
    return !earliest || threshold < earliest ? threshold : earliest;
  }, null);
  const apiQueryParams = {
    only: 'sale',
    bookingStates: ACTIVE_BOOKING_STATES,
    bookingStart: `${queryStart.toISOString()},`,
    sort: 'bookingStart',
    include: ['listing', 'customer', 'booking'],
    'fields.transaction': ['processName', 'lastTransition', 'lastTransitionedAt', 'transitions'],
    'fields.listing': ['title'],
    'fields.booking': ['start', 'state'],
    'fields.user': [
      'profile',
      'profile.displayName',
      'profile.abbreviatedName',
      'deleted',
      'banned',
    ],
    perPage: TRANSACTIONS_PAGE_SIZE,
  };

  const queryPage = (page, accumulatedTransactions = []) =>
    sdk.transactions.query({ ...apiQueryParams, page }).then(response => {
      const transactions = [...accumulatedTransactions, ...denormalisedResponseEntities(response)];
      const upcomingTransactions = selectUpcomingTransactionsForListings(
        transactions,
        bookingListings,
        now
      );
      const totalPages = response?.data?.meta?.totalPages || 1;
      const foundBookingForEveryListing =
        upcomingTransactions.length === bookingListingUuidSet.size;

      return page < totalPages && !foundBookingForEveryListing
        ? queryPage(page + 1, transactions)
        : upcomingTransactions;
    });

  return queryPage(1)
    .then(transactions => {
      dispatch(upcomingTransactionsSuccess(transactions));
      return transactions;
    })
    .catch(e => {
      dispatch(upcomingTransactionsError(storableError(e)));
      return [];
    });
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
export const discardDraft = listingId => (dispatch, getState, sdk) => {
  dispatch(discardDraftRequest(listingId));
  const { queryParams } = getState().ManageListingsPage;

  return sdk.ownListings
    .discardDraft({ id: listingId }, { expand: true })
    .then(() => {
      // Return the listing update with a delay, so that the user
      // notices which listing gets removed
      return Promise.all([delay(300), sdk.ownListings.query(queryParams)]);
    })
    .then(([_, listingResponse]) => {
      dispatch(addOwnEntities(listingResponse));
      dispatch(queryListingsSuccess(listingResponse));
      dispatch(discardDraftSuccess());
    })
    .catch(e => {
      dispatch(discardDraftError(storableError(e)));
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  const states = queryParams.states || MANAGEABLE_LISTING_STATES;
  dispatch(clearOpenListingError());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(
      queryOwnListings({
        ...queryParams,
        states,
        page,
        perPage: RESULT_PAGE_SIZE,
        include: ['images', 'currentStock'],
        'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      })
    ),
  ])
    .then(response => {
      // const currentUser = response[0]?.data?.data;
      const ownListings = response[1]?.data?.data;
      const listings = ownListings || [];

      if (listings.length > 0) {
        return dispatch(fetchUpcomingTransactionsForListings(listings)).then(() => ownListings);
      }

      dispatch(upcomingTransactionsSuccess([]));
      return ownListings;
    })
    .catch(e => {
      throw e;
    });
};
